import { HttpContextToken, HttpEvent, HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, throwError, from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { markRequestComplete } from '../../store/app-init/app-init.actions';
import { Auth0Service } from '../auth/services/auth0.service';

// Marks a request that already used its one handshake-window replay,
// so a repeated 403 falls through to the real redirect below.
const HANDSHAKE_403_RETRIED = new HttpContextToken<boolean>(() => false);

/**
 * Forbidden Interceptor
 *
 * Handles 403 (Forbidden) responses by redirecting to the 403 error page.
 * This interceptor catches all 403 responses from API calls and redirects
 * the user to the error page, which is outside the main layout.
 *
 * A 403 that arrives inside the login-handshake window is treated as the
 * backend transiently rejecting a just-issued token, not a real denial:
 * the request gets one refresh-and-replay before any redirect happens.
 *
 * Important: When a 403 occurs during app initialization (e.g., /me endpoint),
 * we mark the request as "complete" for initialization purposes so the app
 * can bootstrap and render the error page. This prevents the app from being
 * stuck waiting for initialization when we're redirecting to an error page.
 *
 * Note: This interceptor should run after authErrorInterceptor to ensure
 * 401 errors are handled first (token refresh), and only genuine 403 errors
 * trigger the redirect.
 */
/**
 * Endpoints whose 403 means "this account does not have the feature", not
 * "this user may not be here". They are handled by the calling feature instead
 * of redirecting away from the page.
 */
const FORBIDDEN_REDIRECT_EXEMPT_PATHS = ['/gs1/'];

const isRedirectExempt = (url: string): boolean =>
  FORBIDDEN_REDIRECT_EXEMPT_PATHS.some((path) => url.includes(path));

export const forbiddenInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const store = inject(Store);
  const auth0Service = inject(Auth0Service);

  const redirectTo403 = (error: HttpErrorResponse): Observable<never> => {
    // Prevent redirect loop - don't redirect if already on 403 page
    const currentUrl = router.url;

    if (currentUrl.includes('/maintenance/403') || currentUrl === '/403') {
      console.log('[ForbiddenInterceptor] Already on 403 page, skipping redirect');

      return throwError(() => error);
    }

    console.log('[ForbiddenInterceptor] Redirecting to 403 page from:', currentUrl);

    // If this is an initialization request (like /me), mark it as complete
    // so the app can bootstrap and render the error page
    const url = error.url || '';

    if (url.includes('/me')) {
      console.log('[ForbiddenInterceptor] Marking "user" request as complete (403 during init)');
      store.dispatch(markRequestComplete({ requestKey: 'user' }));
    }

    if (url.includes('/profile')) {
      console.log('[ForbiddenInterceptor] Marking "profile" request as complete (403 during init)');
      store.dispatch(markRequestComplete({ requestKey: 'profile' }));
    }

    // Navigate to 403 error page (outside main layout)
    // Use from() to convert Promise to Observable and ensure navigation completes
    return from(router.navigateByUrl('/maintenance/403', { replaceUrl: true })).pipe(
      switchMap((success) => {
        console.log('[ForbiddenInterceptor] Navigation result:', success);

        // After navigation, throw the error to prevent further processing
        // But the navigation should have already happened
        return throwError(() => error);
      }),
      catchError((navError) => {
        console.error('[ForbiddenInterceptor] Navigation error:', navError);

        // Even if navigation fails, throw the original error
        return throwError(() => error);
      })
    );
  };

  const retryWithFreshToken = (
    request: HttpRequest<unknown>,
    handler: HttpHandlerFn,
    error: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> => {
    console.log('[ForbiddenInterceptor] 403 during auth handshake, refreshing token and retrying:', request.url);
    request.context.set(HANDSHAKE_403_RETRIED, true);

    return from(auth0Service.refreshAccessToken()).pipe(
      switchMap((newToken) =>
        handler(
          request.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
            },
          })
        )
      ),
      catchError((retryError: HttpErrorResponse) =>
        retryError?.status === 403 ? redirectTo403(retryError) : throwError(() => retryError || error)
      )
    );
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle 403 Forbidden errors
      if (error.status === 403 && !isRedirectExempt(error.url || req.url)) {
        if (auth0Service.isWithinAuthHandshakeWindow() && !req.context.get(HANDSHAKE_403_RETRIED)) {
          return retryWithFreshToken(req, next, error);
        }

        return redirectTo403(error);
      }

      // Pass through other errors
      return throwError(() => error);
    })
  );
};
